import { useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import { addToast } from 'components/Toast/Toast'
import { ActionCard } from 'components-ref/molecules/ActionCard/ActionCard'
import GroupsDropdown from 'features/GroupsDropdown/GroupsDropdown'
import { Icons } from 'helpers/icons'
import {
    getDefaultStoryVisibilityGroups,
    putStorySettings
} from 'services/endpoints/settings/general/story'
import { getProfileGroups } from 'services/endpoints/social'
import { useUserProfile } from 'store'
import { type IGroup } from 'types/interfaces/ITypes'

interface DefaultStoryGroupsResponse {
    data: {
        can_see_stories: {
            id: string
            name: string
        }[]
    }
}

export const StorySettings = ({ page }: { page: string }) => {
    const [saveStoriesToSharedMedia, setSaveStoriesToSharedMedia] =
        useState(true)
    const [selectedGroups, setSelectedGroups] = useState<IGroup[]>([])
    const userData = useUserProfile()!

    const queryClient = useQueryClient()
    const groupsDropdownRef = useRef<any>()
    const { t } = useTranslation()

    const { data: groups, isLoading: isGroupsLoading } = useQuery(
        'allGroups',
        getProfileGroups,
        {
            refetchOnWindowFocus: false,
            staleTime: 5000
        }
    )

    const {
        data: defaultStoryGroupsData,
        isLoading: isDefaultGroupsLoading
    } = useQuery<DefaultStoryGroupsResponse>(
        'defaultStoryGroups',
        getDefaultStoryVisibilityGroups,
        {
            refetchOnWindowFocus: false,
            staleTime: 5000
        }
    )

    const putSaveStoriesToSharedMedia = useMutation(
        async () =>
            await putStorySettings({
                save_stories_to_shared_media: !saveStoriesToSharedMedia
            }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('user-profile')
            },
            onError: err => {
                console.error(err)
                addToast('error', 'Something went wrong')
            }
        }
    )

    const putStoryVisibilitySetting = useMutation(putStorySettings, {
        onSuccess: () => {
            queryClient.invalidateQueries('user-profile')
            queryClient.invalidateQueries('defaultStoryGroups')
        },
        onError: err => {
            console.error(err)
            addToast('error', t('error:errorSomethingWentWrong'))
        }
    })

    const onDropdownApply = (dropdownSelection: IGroup[]): void => {
        const selectedGroupIds = dropdownSelection.map(value => value.id)
        const { mutate } = putStoryVisibilitySetting

        mutate({ can_see_stories: selectedGroupIds })
    }

    useEffect(() => {
        if (userData && defaultStoryGroupsData && groups) {
            setSaveStoriesToSharedMedia(
                !!userData.save_stories_to_shared_media || false
            )
    
            const groupIdsFromResponse = defaultStoryGroupsData.data.can_see_stories.map(g => g.id)
            const selectedFromResponse: IGroup[] = groups.filter(group =>
                groupIdsFromResponse.includes(group.id)
            )
    
            setSelectedGroups(selectedFromResponse)
        }
    }, [userData, defaultStoryGroupsData, groups])


    if (page !== 'story-settings-main') {
        return <div>Page not found</div>
    }

    return (
        <>
            <GroupsDropdown
                showEmptyGroups={true}
                apply={(selectedValue: IGroup[]) => {
                    onDropdownApply(selectedValue)
                }}
                headText={t('selectAudience')}
                preview={selectedGroups}
                isEdit={true}
                hasCloseButton={true}
                ref={groupsDropdownRef}
            >
                <ActionCard
                    icon={Icons.profile_black}
                    iconSize='small'
                    text={t('whoCanSeeYourStories')}
                    description={
                        selectedGroups.length > 0
                            ? selectedGroups.map(g => g.name).join(', ')
                            : t('selectedGroups')
                    }
                    hasArrow
                    clickFn={() => groupsDropdownRef.current.openDropdown()}
                />
            </GroupsDropdown>
            <ActionCard
                text={t('saveStoriesToSharedMedia')}
                hasSwitch
                toggleActive={saveStoriesToSharedMedia}
                toggleFn={() => {
                    setSaveStoriesToSharedMedia(!saveStoriesToSharedMedia)
                    putSaveStoriesToSharedMedia.mutate()
                }}
            />
        </>
    )
}
